import { Pencil } from 'lucide-react';
import Header from 'components/Profile/Header';
import { getStepById } from 'pages/Profile/helper';
import { SUMMARY_BUILDERS } from './helper';
import Footer from './Footer';
import Picture from './Picture';
import Row from './Row';

const Summary = ({ stepId, data, onEdit = null, inModal = false }) => {
  const step = getStepById(stepId);

  const { sections = [], empty = null, card = null } = SUMMARY_BUILDERS[stepId](data) || {};
  const EmptyIcon = empty?.icon;
  const Card = card?.component;

  const pictures = card?.pictures ?? [];
  const hasPictures = pictures.some(({ url }) => url);

  return (
    <div className={inModal ? '' : 'flex min-h-0 flex-1 flex-col'}>
      <div className={`min-h-0 flex-1 ${inModal ? '' : 'overflow-y-auto px-5 pb-7 pt-5 lg:px-10'}`}>
        <div className={`border-b border-gray-100 ${inModal ? 'mb-4 pb-4' : 'mb-6 pb-5'}`}>
          <Header
            icon={step.icon}
            iconBg={step.iconBg}
            iconColor={step.iconColor}
            title={step.title}
            description={step.subtitle}
            completed
            action={!inModal && (
              <button
                type="button"
                onClick={onEdit}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[10px] border-[1.5px] border-secondary-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-secondary-700 transition-colors hover:border-secondary-300 hover:bg-secondary-50"
              >
                <Pencil size={14} strokeWidth={2.5} />
                Edit
              </button>
            )}
          />
        </div>

        <div className="animate-slide-up space-y-4">
          {empty ? (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-6 py-10 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                <EmptyIcon size={22} className="text-secondary-400" />
              </div>

              <div>
                <p className="text-base font-bold text-secondary-900">{empty.title}</p>
                <p className="mt-1 text-sm text-secondary-500">{empty.description}</p>
              </div>
            </div>
          ) : (
            <>
              {Card && (hasPictures ? (
                <div className={`grid gap-4 ${inModal ? '' : 'lg:grid-cols-2'}`}>
                  <Card {...card.props} />

                  <div className={inModal ? '' : 'lg:relative'}>
                    <div className={`grid gap-3 ${pictures.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} ${inModal ? '' : 'lg:absolute lg:inset-0 lg:grid-rows-[minmax(0,1fr)]'}`}>
                      {pictures.map(({ label, url }) => <Picture key={label} label={label} url={url} fill={!inModal} />)}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mx-auto w-full max-w-lg">
                  <Card {...card.props} />
                </div>
              ))}

              {sections.map(({ title, empty: sectionEmpty = '', fields }) => (
                <div key={title} className="overflow-hidden rounded-2xl border border-gray-200">
                  <p className="border-b border-gray-100 bg-gray-50/70 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-400 sm:px-5">
                    {title}
                  </p>

                  {fields.length ? (
                    <div className="divide-y divide-gray-100">
                      {fields.map((field, index) => <Row key={`${title}-${index}`} {...field} />)}
                    </div>
                  ) : (
                    <p className="px-4 py-3.5 text-sm text-secondary-300 sm:px-5">{sectionEmpty}</p>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {!inModal && <Footer stepId={stepId} />}
    </div>
  );
};

export default Summary;
